import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  TextField,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from '../../api/axios';

const formatMaxSize = (mb) =>
  mb >= 1024 ? `${(mb / 1024).toFixed(1).replace(/\.0$/, '')} ГБ` : `${mb} МБ`;

const VideoUploader = ({
  onUploadSuccess,
  currentVideo,
  videoUrl,
  onUrlChange,
  maxSizeMB = 1536,
  inputId = 'video-upload-input',
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setError('Пожалуйста, выберите видеофайл');
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`Размер файла не должен превышать ${formatMaxSize(maxSizeMB)}`);
      return;
    }

    setError('');
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        let baseUrl;
        if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
          baseUrl = apiUrl.replace('/api', '');
        } else {
          baseUrl = window.location.origin;
        }
        const url = response.data.url.startsWith('http')
          ? response.data.url
          : `${baseUrl}${response.data.url}`;
        onUploadSuccess(url);
      }
    } catch (uploadError) {
      console.error('Ошибка загрузки видео:', uploadError);
      setError(uploadError.response?.data?.error || 'Ошибка при загрузке видео');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      {currentVideo && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Текущее видео:
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {currentVideo}
          </Typography>
          <Button
            size="small"
            color="error"
            onClick={() => onUploadSuccess('')}
            sx={{ mt: 1 }}
          >
            Удалить видео
          </Button>
        </Box>
      )}

      <input
        accept="video/*"
        style={{ display: 'none' }}
        id={inputId}
        type="file"
        onChange={handleFileChange}
        disabled={uploading}
      />
      <label htmlFor={inputId}>
        <Button
          variant="outlined"
          component="span"
          startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          disabled={uploading}
          fullWidth
        >
          {uploading ? 'Загрузка...' : `Загрузить видео (макс ${formatMaxSize(maxSizeMB)})`}
        </Button>
      </label>

      <Typography sx={{ my: 1, textAlign: 'center' }} color="text.secondary">
        или
      </Typography>

      <TextField
        fullWidth
        label="URL видео"
        value={videoUrl || ''}
        onChange={(e) => onUrlChange(e.target.value)}
        placeholder="https://youtube.com/... или https://disk.yandex.ru/..."
        helperText="Поддерживается: YouTube, Vimeo, Яндекс.Диск, прямые ссылки на видео"
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default VideoUploader;
